import dayjs from 'dayjs';
import {
  ListCard,
  ListCardSummary,
  ListCardBody,
  ListCardSection,
  ListCardActions,
  ActionButton,
  StatusPill,
} from './MedicalRecordUI';

const TOKENS = {
  primary: '#c43b71',
  ink: '#1A1A2E',
  inkMuted: '#6B7280',
  surfaceLow: 'rgba(26,26,46,0.03)',
};

const FONT = {
  display: 'var(--font-display), Outfit, system-ui',
  body: 'var(--font-body), Manrope, system-ui',
};

export default function MedicalRecordRow({record, expanded, onToggle, onDelete, onEdit}) {
  const dateStr = dayjs(record.record_date).format('DD MMM YYYY');

  // Try to determine attachment type. If there are multiple, show "Multiple"
  let kindPill = null;
  const files = record.medical_files || [];
  if (files.length === 1) {
    const ext = (files[0].file_name || '').split('.').pop().toLowerCase();
    kindPill = ext === 'pdf' ? 'PDF' : 'Image';
  } else if (files.length > 1) {
    kindPill = 'Multiple Files';
  }

  return (
    <ListCard>
      <ListCardSummary expanded={expanded} onToggle={onToggle}>
        <div style={{flex: 1, minWidth: 0}}>
          <div style={{display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap'}}>
            <p
              style={{
                margin: 0,
                fontFamily: FONT.body,
                fontSize: 14,
                color: TOKENS.inkMuted,
              }}
            >
              {dateStr}
            </p>
            <p
              style={{
                margin: 0,
                fontFamily: FONT.display,
                fontSize: 15,
                fontWeight: 800,
                letterSpacing: '-0.01em',
                color: TOKENS.ink,
              }}
            >
              {record.title}
            </p>
          </div>
        </div>

        {kindPill && (
          <div style={{flexShrink: 0}}>
            <StatusPill>{kindPill}</StatusPill>
          </div>
        )}
      </ListCardSummary>

      {expanded && (
        <ListCardBody>
          {record.note && (
            <ListCardSection>
              <p
                style={{
                  margin: '0 0 8px',
                  fontFamily: FONT.body,
                  fontSize: 13,
                  fontWeight: 600,
                  color: TOKENS.ink,
                }}
              >
                Note
              </p>
              <p
                style={{
                  margin: 0,
                  fontFamily: FONT.body,
                  fontSize: 13,
                  color: TOKENS.inkMuted,
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {record.note}
              </p>
            </ListCardSection>
          )}

          {files.length > 0 && (
            <ListCardSection>
              <p
                style={{
                  margin: '0 0 8px',
                  fontFamily: FONT.body,
                  fontSize: 13,
                  fontWeight: 600,
                  color: TOKENS.ink,
                }}
              >
                {files.length > 1 ? 'Attachments' : 'Attachment'}
              </p>
              <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                {files.map((file) => {
                  const ext = (file.file_name || '').split('.').pop().toLowerCase();
                  const isPdf = ext === 'pdf';

                  return (
                    <div
                      key={file.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 12,
                        background: TOKENS.surfaceLow,
                      }}
                    >
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          flexShrink: 0,
                          background: 'rgba(255,255,255,0.8)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        {!isPdf && file.file_link && (
                          <img
                            src={file.file_link}
                            alt=""
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        )}
                        {isPdf && (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M6 3h8l5 5v13a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z"
                              stroke={TOKENS.primary}
                              strokeWidth="1.8"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M14 3v5h5M8 13h8M8 17h6"
                              stroke={TOKENS.primary}
                              strokeWidth="1.8"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                      </div>
                      <div style={{flex: 1, minWidth: 0}}>
                        <p
                          style={{
                            margin: '0 0 2px',
                            fontFamily: FONT.body,
                            fontSize: 13,
                            fontWeight: 600,
                            color: TOKENS.ink,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {file.file_name}
                        </p>
                      </div>
                      <a
                        href={file.file_link}
                        target="_blank"
                        rel="noreferrer"
                        download={file.file_name}
                        style={{
                          color: TOKENS.primary,
                          fontWeight: 600,
                          fontSize: 12,
                          textDecoration: 'none',
                          padding: '8px 12px',
                          borderRadius: 8,
                          background: 'rgba(196,59,113,0.05)',
                        }}
                      >
                        Download
                      </a>
                    </div>
                  );
                })}
              </div>
            </ListCardSection>
          )}

          <ListCardActions>
            <ActionButton
              label={`Edit ${record.title}`}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 21l.5-5.5L17 3z"
                    stroke={TOKENS.primary}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
            {files.length > 0 && (
              <ActionButton
                label={`Download ${record.title}`}
                onClick={(e) => {
                  e.stopPropagation();
                  files.forEach((file) => {
                    const link = document.createElement('a');
                    link.href = file.file_link;
                    link.download = file.file_name || 'download';
                    link.target = '_blank';
                    link.click();
                  });
                }}
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 16V5M7 10l5 5 5-5M4 17v1.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V17"
                      stroke={TOKENS.primary}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
              />
            )}
            <ActionButton
              label={`Delete ${record.title}`}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M19 6H5v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6m-2 0V4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v2M10 11v6M14 11v6"
                    stroke={TOKENS.primary}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
          </ListCardActions>
        </ListCardBody>
      )}
    </ListCard>
  );
}
